import type { ReactNode } from 'react';
import { SCRAP_SOURCE_LABEL, type ScrapSource, type ScrapStatus, type DisposalMethod } from '@/api/scrap.api';

// Small pieces shared by the scrap screens

export const STATUS_STYLE: Record<ScrapStatus, { label: string; cls: string }> = {
  PENDING: { label: 'Pending', cls: 'bg-amber-50 text-amber-700 border-amber-200' },
  APPROVED: { label: 'In scrap yard', cls: 'bg-indigo-50 text-[#4f46e5] border-indigo-200' },
  DISPOSED: { label: 'Disposed', cls: 'bg-green-50 text-green-700 border-green-200' },
  REJECTED: { label: 'Rejected', cls: 'bg-slate-100 text-slate-500 border-slate-200' },
};

export const SOURCE_STYLE: Record<ScrapSource, { icon: string; cls: string }> = {
  PRODUCTION: { icon: 'ri-settings-3-line', cls: 'text-blue-600 bg-blue-50' },
  GRN: { icon: 'ri-truck-line', cls: 'text-orange-600 bg-orange-50' },
  PROCESS: { icon: 'ri-scissors-cut-line', cls: 'text-violet-600 bg-violet-50' },
  WRITE_OFF: { icon: 'ri-archive-line', cls: 'text-rose-600 bg-rose-50' },
};

export const DISPOSAL_LABEL: Record<DisposalMethod, string> = {
  SOLD: 'Sold',
  DESTROYED: 'Destroyed',
  DONATED: 'Donated',
};

export function StatusBadge({ status, method }: { status: ScrapStatus; method?: DisposalMethod | null }) {
  const s = STATUS_STYLE[status] ?? STATUS_STYLE.PENDING;
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full border text-[11px] font-semibold whitespace-nowrap ${s.cls}`}>
      {status === 'DISPOSED' && method ? DISPOSAL_LABEL[method] : s.label}
    </span>
  );
}

export function SourceTag({ source }: { source: ScrapSource }) {
  const s = SOURCE_STYLE[source] ?? SOURCE_STYLE.PRODUCTION;
  return (
    <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[11px] font-medium whitespace-nowrap ${s.cls}`}>
      <i className={s.icon} /> {SCRAP_SOURCE_LABEL[source]}
    </span>
  );
}

export const inr = (n: number | string | null | undefined) =>
  `₹${Number(n || 0).toLocaleString('en-IN', { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`;

export const qtyText = (n: number | string | null | undefined) =>
  Number(n || 0).toLocaleString('en-IN', { maximumFractionDigits: 4 });

export const dateText = (d?: string | null) =>
  d ? new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';

export const labelCls = 'text-xs font-semibold text-[#64748b] uppercase tracking-wide';
export const inputCls =
  'w-full h-10 px-3 rounded-lg border border-[#e2e8f0] text-sm text-[#1e293b] bg-white focus:outline-none focus:border-[#4f46e5] focus:ring-2 focus:ring-[#4f46e5]/20';

/** Blocks typing a minus / exponent in money and quantity inputs */
export const noNegativeKeys = (e: React.KeyboardEvent<HTMLInputElement>) => {
  if (['-', '+', 'e', 'E'].includes(e.key)) e.preventDefault();
};

export function Modal({ title, subtitle, onClose, children, footer, width = 'max-w-lg' }: {
  title: string;
  subtitle?: ReactNode;
  onClose: () => void;
  children: ReactNode;
  footer: ReactNode;
  width?: string;
}) {
  return (
    <div className="fixed inset-0 z-[600] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className={`relative bg-white rounded-2xl shadow-2xl w-full ${width} max-h-[92vh] flex flex-col`}>
        <div className="flex items-start justify-between gap-3 px-6 py-4 border-b border-[#e2e8f0]">
          <div className="min-w-0">
            <h3 className="text-base font-bold text-[#1e293b]">{title}</h3>
            {subtitle && <div className="text-xs text-[#64748b] mt-0.5">{subtitle}</div>}
          </div>
          <button type="button" onClick={onClose} className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-slate-100 text-[#64748b] cursor-pointer">
            <i className="ri-close-line text-lg" />
          </button>
        </div>
        <div className="flex-1 overflow-y-auto px-6 py-5 space-y-4">{children}</div>
        <div className="flex items-center justify-end gap-2 px-6 py-3 border-t border-[#e2e8f0] bg-[#f8fafc] rounded-b-2xl">{footer}</div>
      </div>
    </div>
  );
}

export function Field({ label, required, error, hint, children }: { label: string; required?: boolean; error?: string; hint?: ReactNode; children: ReactNode }) {
  return (
    <div className="space-y-1.5">
      <label className={labelCls}>
        {label} {required && <span className="text-red-500">*</span>}
      </label>
      {children}
      {error ? <p className="text-xs text-red-500">{error}</p> : hint ? <p className="text-xs text-[#94a3b8]">{hint}</p> : null}
    </div>
  );
}
